import { useState } from "react";

import type { EmpresaOut } from "../../lib/cadastros/tipos";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Formulário controlado de empresa (FE-4 CP-B). Cobre os DOIS modos:
 *
 *  - CRIAÇÃO (`empresa === undefined`): campos cnpj + razao_social + fantasia.
 *  - EDIÇÃO (`empresa` presente): cnpj READ-ONLY (identidade fiscal, não editável
 *    no PATCH do backend), razao_social + fantasia + toggle `ativa`.
 *
 * Componente PURO de apresentação/coleta: não conhece o hook de mutação. O
 * orquestrador (`PaginaEmpresas`) injeta `aoSalvar`, o estado `salvando` e o
 * `erro` do servidor (já normalizado em `ErroHttp`). Espelha o estilo do
 * `ConfirmacaoConciliacao`.
 *
 * Validação de BORDA mínima: cnpj e razao_social obrigatórios na criação. A
 * validação canônica é do backend (422) — aqui só evitamos submeter vazio.
 *
 * SÓ classes Tailwind dos tokens CPMH; labels em todos os inputs; erro com
 * `role="alert"`.
 */

/** Campos coletados pelo formulário. Na edição o `cnpj` é ignorado pelo caller. */
export interface DadosEmpresaForm {
  cnpj: string;
  razao_social: string;
  fantasia: string;
  ativa: boolean;
}

interface FormularioEmpresaProps {
  /** Empresa em edição, ou `undefined` para criação. */
  empresa?: EmpresaOut;
  /** Dispara a mutação real (POST/PATCH) com os dados coletados. */
  aoSalvar: (dados: DadosEmpresaForm) => void;
  /** Fecha o formulário sem salvar. */
  aoCancelar: () => void;
  /** Mutação em voo: desabilita o submit e troca o rótulo. */
  salvando: boolean;
  /** Erro do servidor (normalizado), ou `null`. */
  erro: unknown;
}

export function FormularioEmpresa({
  empresa,
  aoSalvar,
  aoCancelar,
  salvando,
  erro,
}: FormularioEmpresaProps): JSX.Element {
  const ehEdicao = empresa !== undefined;

  const [cnpj, setCnpj] = useState(empresa?.cnpj ?? "");
  const [razaoSocial, setRazaoSocial] = useState(empresa?.razao_social ?? "");
  const [fantasia, setFantasia] = useState(empresa?.fantasia ?? "");
  const [ativa, setAtiva] = useState(empresa?.ativa ?? true);
  const [erroBorda, setErroBorda] = useState<string | null>(null);

  function submeter(): void {
    // Validação de borda: campos obrigatórios na criação.
    if (!ehEdicao && cnpj.trim() === "") {
      setErroBorda("Informe o CNPJ.");
      return;
    }
    if (razaoSocial.trim() === "") {
      setErroBorda("Informe a razão social.");
      return;
    }
    setErroBorda(null);
    aoSalvar({
      cnpj: cnpj.trim(),
      razao_social: razaoSocial.trim(),
      fantasia: fantasia.trim(),
      ativa,
    });
  }

  return (
    <div className="flex max-w-lg flex-col gap-4 rounded-md border border-platinum bg-snow p-6 font-corpo text-base text-off-black">
      <h2 className="font-titulo text-xl text-off-black">
        {ehEdicao ? "Editar empresa" : "Nova empresa"}
      </h2>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">CNPJ</span>
        <input
          type="text"
          value={cnpj}
          onChange={(evento) => setCnpj(evento.target.value)}
          readOnly={ehEdicao}
          aria-readonly={ehEdicao}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black read-only:bg-platinum"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Razão social</span>
        <input
          type="text"
          value={razaoSocial}
          onChange={(evento) => setRazaoSocial(evento.target.value)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Nome fantasia</span>
        <input
          type="text"
          value={fantasia}
          onChange={(evento) => setFantasia(evento.target.value)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        />
      </label>

      {ehEdicao && (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={ativa}
            onChange={(evento) => setAtiva(evento.target.checked)}
          />
          <span className="font-subtitulo">Ativa</span>
        </label>
      )}

      {erroBorda !== null && (
        <p role="alert" className="font-corpo text-base text-flame">
          {erroBorda}
        </p>
      )}

      {erro !== null && erro !== undefined && (
        <p role="alert" className="font-corpo text-base text-flame">
          {mensagemErroEmpresa(erro)}
        </p>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={aoCancelar}
          disabled={salvando}
          className="rounded-md border border-platinum bg-snow px-4 py-1 text-off-black disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submeter}
          disabled={salvando}
          className="rounded-md border border-flame bg-flame px-4 py-1 text-snow disabled:opacity-60"
        >
          {salvando ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </div>
  );
}

/**
 * Traduz o erro de uma mutação de empresa numa mensagem estável. 409 (conflito —
 * ex.: CNPJ duplicado) e 422 (validação) recebem mensagens claras; demais reusam
 * a mensagem genérica do `ErroHttp` (404 já é genérica anti-enumeração). Erro
 * não-`ErroHttp` (defesa) → fallback genérico.
 */
function mensagemErroEmpresa(erro: unknown): string {
  if (erro instanceof ErroHttp) {
    if (erro.status === 409) {
      return "Já existe uma empresa com este CNPJ.";
    }
    if (erro.status === 422) {
      return "Dados inválidos. Verifique os campos e tente novamente.";
    }
    return erro.message;
  }
  return "Não foi possível salvar a empresa.";
}
